"use client";
import React, { useCallback, useEffect } from "react";
import Link from "next/link";
import { signOut, useSession } from "next-auth/react";
import { useState } from "react";
import { useDrawer } from "@/utils/DrawerProvider";
import { UserSession } from "@/utils/types";
import Logo from "@/public/logo.png";
import Image from "next/image";
import { useDialog } from "@/utils/DialogProvider";
import { PrimaryBtn, SecondaryBtn } from "../Buttons";
import { Avatar, Badge, IconButton, Skeleton } from "@mui/material";
import { FaPowerOff } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useRouter, useSearchParams } from "next/navigation";
import { usePathname } from "next/navigation";
import i18nConfig from "@/app/i18nConfig";
import ReactFlagsSelect from "react-flags-select";
import { styled } from "@mui/system";
import CircularProgress from "@mui/material/CircularProgress";
import { getSocket } from "@/utils/sharedUtils";

// --- Extracted Component for Navigation Link --- //
const NavItem = ({ href, condition, children }: any) => {
  const pathName = usePathname();
  return (
    <li className={condition(pathName) ? "text-hover light-blue fw800" : "dark-grey text-hover"}>
      <Link href={href}>{children}</Link>
    </li>
  );
};

// --- Main Header Component --- //
export const Header = () => {
  const session = useSession().data as UserSession;
  const { openDialog, closeDialog } = useDialog();
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language as "ar" | "fr" | "en";

  const router = useRouter();
  const pathName = usePathname();
  const searchParams = useSearchParams();

  const socket = getSocket();
  const [lang, setLang] = useState(currentLocale === "fr" ? "FR" : currentLocale === "en" ? "US" : "DZ");
  const [notif, setNotif] = useState(" ");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (socket) {
      socket.emit("join_room", { roomId: `${session?.id}`, id: "header" });
      socket.on("receive_msg", (data) => setNotif(data.new));
    }
  }, [socket]);

  const handleChange = useCallback(
    (newLocale: string) => () => {
      setIsLoading(true);
      const date = new Date();
      date.setTime(date.getTime() + 30 * 24 * 60 * 60 * 1000);
      document.cookie = `NEXT_LOCALE=${newLocale};expires=${date.toUTCString()};path=/`;

      const current = new URLSearchParams(Array.from(searchParams.entries()));
      const search = current.toString();

      const newPath = currentLocale === i18nConfig.defaultLocale
        ? `/${newLocale}${pathName}${searchParams.size ? `?${search}` : ""}`
        : `${pathName.replace(`/${currentLocale}`, `/${newLocale}`)}${searchParams.size ? `?${search}` : ""}`;

      router.push(newPath);
      router.refresh();
      setIsLoading(false);
    },
    [currentLocale, pathName, router, searchParams]
  );

  return (
    <div className="navigation">
      <br /><br /><br /><br />
      <div className={`horizontal-nav-bar gap40 mc1 web-only ${currentLocale === "ar" ? "rtl" : ""}`}>
        <Link href="/">
          <Image src={Logo} alt="Logo" className="h40px pointer" width={100} />
          <p className="f12 tcenter">Amazigh group</p>
        </Link>

        <ul className="flex gap40">
          <NavItem href="/make-to-order" condition={(path: string) => path.endsWith("make-to-order")}>{t("Dubai Import")}</NavItem>
          <NavItem href="/import_china" condition={(path: string) => path.endsWith("import_china")}>{t("China Import")}</NavItem>
          <NavItem href="/sourcing" condition={(path: string) => path.endsWith("/sourcing")}>{t("sourcing")}</NavItem>
          <NavItem href="/shop" condition={(path: string) => path.includes("/shop")}>{t("shop")}</NavItem>
          <NavItem href="/cars" condition={(path: string) => path.includes("/cars")}>{t("cars")}</NavItem>
          <NavItem href="/faq" condition={(path: string) => path.endsWith("faq")}>{t("FAQ's")}</NavItem>
          <NavItem href="/about" condition={(path: string) => path.endsWith("about")}>{t("about")}</NavItem>
        </ul>

        <ul className="flex gap20 vcenter">
          {session ? (
            <>
              <NavItem
                href={session?.role === "user" ? "/my_space?tab=orders" : ["admin", "super-admin"].includes(session?.role!) ? "/admin" : "/"}
                condition={(path: string) => path.includes("/my_space") || path.includes("/admin")}
              >
                {notif === "my_space" ? (
                  <Badge onClick={() => setNotif(" ")}>{t("mySpace")}</Badge>
                ) : (
                  <div className="flex">{t("mySpace")}</div>
                )}
              </NavItem>

              <NavItem href="/profile" condition={(path: string) => path.includes("/profile")}>
                <Avatar src={`/api/images/uploads/${session?.profile_pic}`} />
              </NavItem>

              <li className="pointer">
                <IconButton
                  size="small"
                  className="br100"
                  onClick={() =>
                    openDialog(
                      t("logout"),
                      <>
                        <div className="dialog-body">
                          <p>{t("logoutConfirmation")}</p>
                        </div>
                        <div className="dialog-footer">
                          <div></div>
                          <div className="flex gap10">
                            <SecondaryBtn text={t("cancel")} submit={closeDialog} />
                            <PrimaryBtn
                              text={t("validate")}
                              submit={async () => {
                                await signOut({ callbackUrl: "/" });
                                return { success: true };
                              }}
                            />
                          </div>
                        </div>
                      </>
                    )
                  }
                >
                  <FaPowerOff className="red" />
                </IconButton>
              </li>
            </>
          ) : (
            <>
              <NavItem href="/login" condition={(path: string) => path.includes("/login")}>{t("login")}</NavItem>
              <NavItem href="/signup" condition={(path: string) => path.includes("/signup")}>{t("signup")}</NavItem>
            </>
          )}

          {isLoading ? (
            <div><CircularProgress /></div>
          ) : (
            <ReactFlagsSelectStyled
              selected={lang}
              onSelect={(e) => {
                setLang(e);
                handleChange(e === "FR" ? "fr" : e === "US" ? "en" : "ar")();
              }}
              countries={["FR", "US", "DZ"]}
              showSelectedLabel={false}
              showOptionLabel={false}
              selectButtonClassName="menu-flags-button"
              placeholder=" "
            />
          )}
        </ul>
      </div>
    </div>
  );
};
export const ReactFlagsSelectStyled = styled(ReactFlagsSelect)`
  .ReactFlagsSelect-module_selectBtn__19wW7:after,
  .ReactFlagsSelect-module_selectBtn__19wW7[aria-expanded="true"]:after {
    display: none;
  }
  .ReactFlagsSelect-module_selectBtn__19wW7 {
    padding: 0px;
    border: 0px solid #eee;
  }
  .ReactFlagsSelect-module_selectOption__3pcgW {
    padding: 0px 8px;
  }
  .ReactFlagsSelect-module_selectOptions__3LNBJ {
    margin-top: 0px;
  }

  .ReactFlagsSelect-module_flagsSelect__2pfa2 {
    padding-bottom: 0px;
  }
`;


